import React, { useEffect, useMemo, useState } from "react";
import { Group, SegmentedControl, Select, Skeleton } from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { EmptyState, SectionCard, radiusPx } from "@xplatform/design-system";
import { IconChartBar } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { MonitoringDimension, MonitoringTimeSeries } from "@models/adminMonitoring";
import { formatCount, formatMoney, moduleLabel } from "@utils/monitoringFormat";
import { OTHER_COLOR, OTHER_SERIES, SERIES_COLORS } from "./palette";

type Metric = "cost" | "events" | "tokens" | "pages";

/** The value one point contributes to a metric (cost is per currency, the rest span currencies). */
const valueOf = (p: MonitoringTimeSeries["points"][number], metric: Metric): number => {
  if (metric === "cost") return Number(p.total_cost);
  if (metric === "tokens") return (p.input_tokens ?? 0) + (p.output_tokens ?? 0);
  if (metric === "pages") return p.pages_processed ?? 0;
  return p.event_count;
};

interface UsageChartProps {
  title: string;
  data: MonitoringTimeSeries | null;
  loading: boolean;
  /** When set, a selector for the split dimension. */
  splitOptions?: MonitoringDimension[];
  onSplitChange?: (dimension: MonitoringDimension) => void;
}

const bucketLabel = (iso: string, granularity: MonitoringTimeSeries["granularity"]) =>
  dayjs(iso).format(granularity === "hour" ? "MMM D HH:mm" : granularity === "month" ? "MMM YYYY" : "MMM D");

/**
 * Stacked bars of cost or events per time bucket, one stack segment per series
 * (at most SERIES_COLORS.length; the rest fold into "Other"). Cost is drawn for
 * one currency at a time; event counts span every currency.
 */
const UsageChart: React.FC<UsageChartProps> = ({ title, data, loading, splitOptions, onSplitChange }) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<Metric>("cost");
  const currencies = useMemo(() => {
    const totals = new Map<string, number>();
    for (const p of data?.points ?? []) totals.set(p.currency, (totals.get(p.currency) ?? 0) + p.event_count);
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [data]);
  const [currency, setCurrency] = useState<string | null>(null);
  useEffect(() => {
    if (!currency || !currencies.includes(currency)) setCurrency(currencies[0] ?? null);
  }, [currencies, currency]);

  const { rows, series } = useMemo(() => {
    if (!data) return { rows: [], series: [] };
    const visible = data.series.slice(0, SERIES_COLORS.length);
    const folded = data.series.length > visible.length;
    const keyOf = (s: string) => (visible.includes(s) ? s : OTHER_SERIES);
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const bucket of data.buckets) byBucket.set(bucket, { bucket: bucketLabel(bucket, data.granularity) });
    for (const p of data.points) {
      if (metric === "cost" && p.currency !== currency) continue;
      const row = byBucket.get(p.bucket) ?? { bucket: bucketLabel(p.bucket, data.granularity) };
      const key = keyOf(p.series);
      const value = valueOf(p, metric);
      row[key] = ((row[key] as number | undefined) ?? 0) + value;
      byBucket.set(p.bucket, row);
    }
    const label = (s: string) =>
      s === "total" ? t("admin.insights.chart.total") : data.split_by === "module" ? moduleLabel(s) : s;
    return {
      rows: [...byBucket.values()],
      series: [
        ...visible.map((s, i) => ({ name: s, label: label(s), color: SERIES_COLORS[i] })),
        ...(folded ? [{ name: OTHER_SERIES, label: t("admin.insights.chart.other"), color: OTHER_COLOR }] : []),
      ],
    };
  }, [data, metric, currency, t]);

  const actions = (
    <Group gap="xs">
      {splitOptions && onSplitChange && (
        <Select
          size="xs"
          w={150}
          data={splitOptions.map((d) => ({ value: d, label: t(`admin.insights.dimensions.${d}`) }))}
          value={data?.split_by ?? null}
          onChange={(value) => value && onSplitChange(value as MonitoringDimension)}
          aria-label={t("admin.insights.chart.splitBy")}
          allowDeselect={false}
        />
      )}
      {metric === "cost" && currencies.length > 1 && (
        <Select size="xs" w={90} data={currencies} value={currency} onChange={setCurrency}
                aria-label={t("admin.insights.filters.currency")} allowDeselect={false} />
      )}
      <SegmentedControl
        size="xs"
        value={metric}
        onChange={(value) => setMetric(value as Metric)}
        data={(["cost", "events", "tokens", "pages"] as Metric[]).map((value) => ({
          value, label: t(`admin.insights.chart.${value}`),
        }))}
      />
    </Group>
  );

  return (
    <SectionCard title={title} actions={actions}>
      {loading && !data ? (
        <Skeleton h={300} />
      ) : !data?.points.length ? (
        <EmptyState icon={<IconChartBar size={32} />} title={t("admin.insights.empty.title")}
                    description={t("admin.insights.empty.window")} />
      ) : (
        <BarChart
          h={300}
          data={rows}
          dataKey="bucket"
          type="stacked"
          series={series}
          withLegend={series.length > 1}
          legendProps={{ verticalAlign: "bottom" }}
          tickLine="none"
          gridAxis="y"
          valueFormatter={(value) =>
            metric === "cost" && currency ? formatMoney(value, currency) : formatCount(value)}
          barProps={{ radius: [radiusPx.xs, radiusPx.xs, 0, 0] }}
        />
      )}
    </SectionCard>
  );
};

export default UsageChart;
